import React, { useState } from 'react'
import { Flex } from '@dostavkee/web-ui'
import { useNavigate } from '@tanstack/react-router'

import { useDeviceDetect } from '@/shared/model'
import { FooterMobile } from '@/shared/ui/footer-mobile'
import { HeaderDesktop } from '@/shared/ui/header-desktop'
import { HeaderMobile } from '@/shared/ui/header-mobile'
import { TotalCount } from '@/shared/ui/total-count'

import { useRecruitmentSearch } from '../../model/use-recruitment-search'
import { useRecruitmentTotal } from '../../model/use-recruitment-total'
import { RecruitmentCards } from '../recruitment-cards/recruitment-cards'
import { RecruitmentCreateButton } from '../recruitment-create-button'
import { RecruitmentSearchChips } from '../recruitment-search-chip/recruitment-search-chips'
import { RecruitmentSearchDrawer } from '../recruitment-search-drawer'
import {
    RecruitmentSearchForm,
    type RecruitmentSearchFormValues,
} from '../recruitment-search-form/recruitment-search-form'
import { RecruitmentTable } from '../recruitment-table/recruitment-table'
import styles from './recruitment-page.module.scss'

export const RecruitmentPage = () => {
    const navigate = useNavigate()
    const { isMobile, isTablet } = useDeviceDetect()
    const [isSearchDrawerOpen, setIsSearchDrawerOpen] = useState(false)
    const { isSearch, searchValues } = useRecruitmentSearch()

    const { recruitmentTotal } = useRecruitmentTotal()

    const handleSubmit = React.useCallback(
        (values: RecruitmentSearchFormValues) => {
            const search: Record<string, string | string[] | undefined> = {}

            if (values.phone) {
                search.phone = values.phone
            }

            if (values.date_range?.from && values.date_range?.to) {
                search.from_date = values.date_range.from
                search.to_date = values.date_range.to
            }

            if (values.recruiter_id) {
                search.recruiter_id = values.recruiter_id
            }

            navigate({
                to: '/recruitment',
                search: {
                    ...search,
                    page: 1,
                },
            })

            if (isMobile || isTablet) {
                setIsSearchDrawerOpen(false)
            }
        },
        [isMobile, isTablet, navigate]
    )

    const handleReset = React.useCallback(() => {
        navigate({
            to: '/recruitment',
            search: {
                page: 1,
            },
        })

        if (isMobile || isTablet) {
            setIsSearchDrawerOpen(false)
        }
    }, [isMobile, isTablet, navigate])

    if (isMobile || isTablet) {
        return (
            <section className={styles['recruitment-page']}>
                <HeaderMobile
                    footer={isSearch && <RecruitmentSearchChips />}
                    variant={isSearch ? 'default' : 'gradient'}
                    postfixElement={
                        <RecruitmentSearchDrawer
                            open={isSearchDrawerOpen}
                            searchValues={{
                                ...searchValues,
                                date_range: {
                                    from: searchValues.from_date,
                                    to: searchValues.to_date,
                                },
                            }}
                            onClose={() => setIsSearchDrawerOpen(false)}
                            onOpenChange={setIsSearchDrawerOpen}
                            onReset={handleReset}
                            onSubmit={handleSubmit}
                        />
                    }
                    title={
                        <Flex align='center' direction='row' justify='center' spacing='xs'>
                            <HeaderMobile.Title title='Заявки' />
                            <TotalCount count={recruitmentTotal} />
                        </Flex>
                    }
                />

                <RecruitmentCards />

                <FooterMobile>
                    <RecruitmentCreateButton />
                </FooterMobile>
            </section>
        )
    }

    return (
        <section className={styles['recruitment-page']}>
            <HeaderDesktop
                title='Заявки'
                postfixElement={
                    <div className={styles['recruitment-page__header-controls']}>
                        <TotalCount count={recruitmentTotal} />
                        <RecruitmentCreateButton />
                    </div>
                }
            />

            <RecruitmentSearchForm
                searchValues={{
                    ...searchValues,
                    date_range: {
                        from: searchValues.from_date,
                        to: searchValues.to_date,
                    },
                }}
                onReset={handleReset}
                onSubmit={handleSubmit}
            />

            <RecruitmentTable onReset={handleReset} />
        </section>
    )
}
